import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, CheckCircle, Mail, MessageSquare, AlertCircle, Eye, EyeOff, Check, X, ChevronDown, Star, TrendingUp, Shield, Zap, Sparkles, Users } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { OTPVerification } from '../components/auth/OTPVerification';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../hooks/useLanguage';
import { validatePassword, isEmailExists, isPhoneExists, validateEmail, hashPassword } from '../utils/security';
import { SEO } from '../components/SEO';
type CountryCode = string;
interface Country {
  code: CountryCode;
  name: string;
  nameAr: string;
  flag: string;
  dialCode: string;
  placeholder: string;
  webhookUrl: string;
}
const countries: Country[] = [
// Primary Countries
{
  code: 'eg',
  name: 'Egypt',
  nameAr: 'مصر',
  flag: '🇪🇬',
  dialCode: '+20',
  placeholder: '1xxxxxxxxx',
  webhookUrl: 'https://n8n.operines.me/webhook/otp-whatsapp-operines-eg'
}, {
  code: 'ae',
  name: 'UAE',
  nameAr: 'الإمارات',
  flag: '🇦🇪',
  dialCode: '+971',
  placeholder: '5xxxxxxxx',
  webhookUrl: 'https://n8n.operines.me/webhook/otp-whatsapp-operines-ae'
}, {
  code: 'sa',
  name: 'Saudi Arabia',
  nameAr: 'السعودية',
  flag: '🇸🇦',
  dialCode: '+966',
  placeholder: '5xxxxxxxx',
  webhookUrl: 'https://n8n.operines.me/webhook/otp-whatsapp-operines-ae' // Using same webhook as UAE
},
// Other Arab Countries
{
  code: 'iq',
  name: 'Iraq',
  nameAr: 'العراق',
  flag: '🇮🇶',
  dialCode: '+964',
  placeholder: 'xxxxxxxxx',
  webhookUrl: 'https://n8n.operines.me/webhook/otp-whatsapp-operines-ae'
}, {
  code: 'kw',
  name: 'Kuwait',
  nameAr: 'الكويت',
  flag: '🇰🇼',
  dialCode: '+965',
  placeholder: 'xxxxxxxx',
  webhookUrl: 'https://n8n.operines.me/webhook/otp-whatsapp-operines-ae'
}, {
  code: 'bh',
  name: 'Bahrain',
  nameAr: 'البحرين',
  flag: '🇧🇭',
  dialCode: '+973',
  placeholder: 'xxxxxxxx',
  webhookUrl: 'https://n8n.operines.me/webhook/otp-whatsapp-operines-ae'
}, {
  code: 'qa',
  name: 'Qatar',
  nameAr: 'قطر',
  flag: '🇶🇦',
  dialCode: '+974',
  placeholder: 'xxxxxxxx',
  webhookUrl: 'https://n8n.operines.me/webhook/otp-whatsapp-operines-ae'
}, {
  code: 'om',
  name: 'Oman',
  nameAr: 'عمان',
  flag: '🇴🇲',
  dialCode: '+968',
  placeholder: 'xxxxxxxx',
  webhookUrl: 'https://n8n.operines.me/webhook/otp-whatsapp-operines-ae'
}, {
  code: 'jo',
  name: 'Jordan',
  nameAr: 'الأردن',
  flag: '🇯🇴',
  dialCode: '+962',
  placeholder: 'xxxxxxxxx',
  webhookUrl: 'https://n8n.operines.me/webhook/otp-whatsapp-operines-ae'
}, {
  code: 'lb',
  name: 'Lebanon',
  nameAr: 'لبنان',
  flag: '🇱🇧',
  dialCode: '+961',
  placeholder: 'xxxxxxxx',
  webhookUrl: 'https://n8n.operines.me/webhook/otp-whatsapp-operines-ae'
}, {
  code: 'sy',
  name: 'Syria',
  nameAr: 'سوريا',
  flag: '🇸🇾',
  dialCode: '+963',
  placeholder: 'xxxxxxxxx',
  webhookUrl: 'https://n8n.operines.me/webhook/otp-whatsapp-operines-ae'
}, {
  code: 'ly',
  name: 'Libya',
  nameAr: 'ليبيا',
  flag: '🇱🇾',
  dialCode: '+218',
  placeholder: 'xxxxxxxxx',
  webhookUrl: 'https://n8n.operines.me/webhook/otp-whatsapp-operines-ae'
}, {
  code: 'tn',
  name: 'Tunisia',
  nameAr: 'تونس',
  flag: '🇹🇳',
  dialCode: '+216',
  placeholder: 'xxxxxxxx',
  webhookUrl: 'https://n8n.operines.me/webhook/otp-whatsapp-operines-ae'
}, {
  code: 'dz',
  name: 'Algeria',
  nameAr: 'الجزائر',
  flag: '🇩🇿',
  dialCode: '+213',
  placeholder: 'xxxxxxxxx',
  webhookUrl: 'https://n8n.operines.me/webhook/otp-whatsapp-operines-ae'
}, {
  code: 'ma',
  name: 'Morocco',
  nameAr: 'المغرب',
  flag: '🇲🇦',
  dialCode: '+212',
  placeholder: 'xxxxxxxxx',
  webhookUrl: 'https://n8n.operines.me/webhook/otp-whatsapp-operines-ae'
}, {
  code: 'sd',
  name: 'Sudan',
  nameAr: 'السودان',
  flag: '🇸🇩',
  dialCode: '+249',
  placeholder: 'xxxxxxxxx',
  webhookUrl: 'https://n8n.operines.me/webhook/otp-whatsapp-operines-ae'
}, {
  code: 'ye',
  name: 'Yemen',
  nameAr: 'اليمن',
  flag: '🇾🇪',
  dialCode: '+967',
  placeholder: 'xxxxxxxxx',
  webhookUrl: 'https://n8n.operines.me/webhook/otp-whatsapp-operines-ae'
}, {
  code: 'ps',
  name: 'Palestine',
  nameAr: 'فلسطين',
  flag: '🇵🇸',
  dialCode: '+970',
  placeholder: 'xxxxxxxxx',
  webhookUrl: 'https://n8n.operines.me/webhook/otp-whatsapp-operines-ae'
}, {
  code: 'mr',
  name: 'Mauritania',
  nameAr: 'موريتانيا',
  flag: '🇲🇷',
  dialCode: '+222',
  placeholder: 'xxxxxxxx',
  webhookUrl: 'https://n8n.operines.me/webhook/otp-whatsapp-operines-ae'
}, {
  code: 'so',
  name: 'Somalia',
  nameAr: 'الصومال',
  flag: '🇸🇴',
  dialCode: '+252',
  placeholder: 'xxxxxxxxx',
  webhookUrl: 'https://n8n.operines.me/webhook/otp-whatsapp-operines-ae'
}, {
  code: 'dj',
  name: 'Djibouti',
  nameAr: 'جيبوتي',
  flag: '🇩🇯',
  dialCode: '+253',
  placeholder: 'xxxxxxxx',
  webhookUrl: 'https://n8n.operines.me/webhook/otp-whatsapp-operines-ae'
}, {
  code: 'km',
  name: 'Comoros',
  nameAr: 'جزر القمر',
  flag: '🇰🇲',
  dialCode: '+269',
  placeholder: 'xxxxxxx',
  webhookUrl: 'https://n8n.operines.me/webhook/otp-whatsapp-operines-ae'
}];
export function RegisterPage() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    brandName: '',
    email: '',
    phone: '',
    password: ''
  });
  const [selectedCountry, setSelectedCountry] = useState<Country>(countries[0]);
  const [isCountryOpen, setIsCountryOpen] = useState(false);
  const countryRef = useRef<HTMLDivElement>(null);
  const [otpMethod, setOtpMethod] = useState<'whatsapp' | 'email'>('whatsapp');
  const [generatedOTP, setGeneratedOTP] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<{
    email?: string;
    phone?: string;
    password?: string[];
    general?: string;
  }>({});
  const [passwordStrength, setPasswordStrength] = useState<'weak' | 'medium' | 'strong'>('weak');
  const {
    register
  } = useAuth();
  const {
    language,
    t
  } = useLanguage();
  const navigate = useNavigate();
  const isRTL = language === 'ar';
  // Close country dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (countryRef.current && !countryRef.current.contains(event.target as Node)) {
        setIsCountryOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Validate password on change
  useEffect(() => {
    if (formData.password) {
      const validation = validatePassword(formData.password, isRTL);
      setPasswordStrength(validation.strength);
      if (!validation.isValid) {
        setErrors(prev => ({
          ...prev,
          password: validation.errors
        }));
      } else {
        setErrors(prev => ({
          ...prev,
          password: undefined
        }));
      }
    }
  }, [formData.password, isRTL]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Clear specific error when user types
    if (name === 'email') {
      setErrors(prev => ({
        ...prev,
        email: undefined
      }));
    }
    if (name === 'phone') {
      setErrors(prev => ({
        ...prev,
        phone: undefined
      }));
    }
  };
  const getFullPhoneNumber = () => {
    const phoneWithoutLeadingZero = formData.phone.startsWith('0') ? formData.phone.slice(1) : formData.phone;
    return `${selectedCountry.dialCode}${phoneWithoutLeadingZero}`;
  };
  const validatePhoneForCountry = (phone: string, country: CountryCode): boolean => {
    const cleanPhone = phone.replace(/[\s-]/g, '').replace(/^0/, '');
    if (country === 'eg') {
      // Egyptian: 10 digits starting with 1
      return /^1[0125][0-9]{8}$/.test(cleanPhone);
    } else if (country === 'ae') {
      // UAE: 9 digits starting with 5
      return /^5[0-9]{8}$/.test(cleanPhone);
    } else if (country === 'sa') {
      // Saudi: 9 digits starting with 5
      return /^5[0-9]{8}$/.test(cleanPhone);
    }
    // For all other countries, lenient validation: 7-12 digits
    return /^[0-9]{7,12}$/.test(cleanPhone);
  };
  const getPhoneErrorMessage = (country: CountryCode): string => {
    if (country === 'eg') {
      return t('error.phoneInvalidEg');
    } else if (country === 'ae') {
      return t('error.phoneInvalidAe');
    } else if (country === 'sa') {
      return isRTL ? 'رقم الهاتف غير صالح (يجب أن يبدأ بـ 05 ويكون 10 أرقام)' : 'Invalid phone number (must start with 05 and be 10 digits)';
    }
    return isRTL ? 'رقم الهاتف غير صالح' : 'Invalid phone number';
  };
  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};
    // Validate email format
    if (!validateEmail(formData.email)) {
      newErrors.email = t('error.emailInvalid');
    }
    // Check if email exists
    else if (isEmailExists(formData.email)) {
      newErrors.email = t('error.emailExists');
    }
    // Validate phone format for selected country
    if (!validatePhoneForCountry(formData.phone, selectedCountry.code)) {
      newErrors.phone = getPhoneErrorMessage(selectedCountry.code);
    }
    // Check if phone exists
    else if (isPhoneExists(getFullPhoneNumber())) {
      newErrors.phone = t('error.phoneExists');
    }
    // Validate password
    const passwordValidation = validatePassword(formData.password, isRTL);
    if (!passwordValidation.isValid) {
      newErrors.password = passwordValidation.errors;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const sendOTPToWebhook = async (phone: string, name: string, email: string, method: 'whatsapp' | 'email'): Promise<string> => {
    // Generate 6-digit OTP
    const otp = Math.floor(100000 + Math.random() * 900000).toString();
    // Use the webhook URL from selected country
    const webhookUrl = selectedCountry.webhookUrl;
    const payload = {
      phone: getFullPhoneNumber(),
      name,
      email,
      otp,
      method,
      country: selectedCountry.code,
      timestamp: new Date().toISOString()
    };
    try {
      await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
    } catch (error) {
      // Try with no-cors mode
      await fetch(webhookUrl, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
    }
    console.log(`OTP sent to ${webhookUrl}:`, {
      ...payload,
      otp: '[HIDDEN]'
    });
    return otp;
  };
  const handleInitialSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Validate form
    if (!validateForm()) {
      return;
    }
    setIsLoading(true);
    try {
      const otp = await sendOTPToWebhook(getFullPhoneNumber(), formData.name, formData.email, otpMethod);
      setGeneratedOTP(otp);
      setStep(2);
    } catch (error) {
      console.error(error);
      setErrors({
        general: t('auth.otpSendFailed')
      });
    } finally {
      setIsLoading(false);
    }
  };
  const handleOTPComplete = async (code: string) => {
    setIsLoading(true);
    try {
      // Verify OTP
      if (code === generatedOTP) {
        // Hash password before saving
        const hashedPassword = await hashPassword(formData.password);
        await register({
          ...formData,
          phone: getFullPhoneNumber(),
          password: hashedPassword,
          country: selectedCountry.code,
          brandName: formData.brandName
        });
        // Save country preference
        localStorage.setItem('operines_country', selectedCountry.code);
        setStep(3);
        setTimeout(() => navigate('/dashboard'), 2000);
      } else {
        setErrors({
          general: t('auth.otpInvalid')
        });
      }
    } catch (error) {
      console.error(error);
      setErrors({
        general: t('auth.verificationError')
      });
    } finally {
      setIsLoading(false);
    }
  };
  const handleResendOTP = async () => {
    try {
      const otp = await sendOTPToWebhook(getFullPhoneNumber(), formData.name, formData.email, otpMethod);
      setGeneratedOTP(otp);
    } catch (error) {
      console.error(error);
    }
  };
  const handleSwitchMethod = async (newMethod: 'whatsapp' | 'email') => {
    try {
      setOtpMethod(newMethod);
      const otp = await sendOTPToWebhook(getFullPhoneNumber(), formData.name, formData.email, newMethod);
      setGeneratedOTP(otp);
    } catch (error) {
      console.error(error);
    }
  };
  const getPageTitle = () => {
    if (step === 1) return t('auth.register');
    if (step === 2) {
      return otpMethod === 'email' ? t('auth.confirmEmail') : t('auth.confirmPhone');
    }
    return t('auth.registerSuccess');
  };
  const getStrengthColor = () => {
    switch (passwordStrength) {
      case 'strong':
        return 'bg-green-500';
      case 'medium':
        return 'bg-yellow-500';
      default:
        return 'bg-red-500';
    }
  };
  const getStrengthText = () => {
    switch (passwordStrength) {
      case 'strong':
        return t('password.strong');
      case 'medium':
        return t('password.medium');
      default:
        return t('password.weak');
    }
  };
  return <div className="min-h-screen bg-white flex" dir={isRTL ? 'rtl' : 'ltr'} data-id="element-2655">
      <SEO title={isRTL ? 'إنشاء حساب - تجربة مجانية 3 أيام' : 'Register - 3 Days Free Trial'} description={isRTL ? 'أنشئ حسابك في Operines واستمتع بتجربة مجانية 3 أيام. ابدأ أتمتة واتساب بالذكاء الاصطناعي فوراً بدون بطاقة ائتمان.' : 'Create your Operines account and enjoy a 3-day free trial. Start WhatsApp AI automation instantly with no credit card required.'} canonicalPath="/register" language={language as 'ar' | 'en'} data-id="element-2656" />

      {/* LEFT SIDE - FORM */}
      <div className="flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-20 xl:px-24 bg-white relative z-10" data-id="element-2657">
        <div className="w-full max-w-md mx-auto lg:mx-0 lg:max-w-lg py-8 lg:py-12" data-id="element-2658">
          {/* Logo */}
          <Link to="/" className="inline-block mb-8 lg:mb-12" data-id="element-2659">
            <img src="https://cdn.magicpatterns.com/uploads/8sRwXksp5brxBQY4QcgZ5M/U062aU0635U0645U064aU0645_U0628U062fU0648U0646_U0639U0646U0648U0627U0646_(37).png" alt="Operines" className="h-8 sm:h-10 w-auto" data-id="element-2660" />
          </Link>

          {/* Heading */}
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-gray-900 mb-4" data-id="element-2661">
            {getPageTitle()}
          </h2>

          {step === 1 && <div className="mb-8" data-id="element-2662">
              <p className="text-base text-gray-600" data-id="element-2663">
                {isRTL ? 'أرسل أول رسالة في دقائق. هل لديك حساب بالفعل؟' : 'Send your first message in minutes. Already have an account?'}{' '}
                <Link to="/login" className="font-semibold text-[#4f1964] hover:text-[#3d1350] transition-colors underline decoration-2 decoration-transparent hover:decoration-[#4f1964]" data-id="element-2664">
                  {t('auth.login')}
                </Link>
              </p>
            </div>}

          {/* Form Container */}
          <div className="bg-white shadow-xl lg:shadow-none rounded-2xl lg:rounded-none p-6 lg:p-0 border lg:border-0 border-gray-100" data-id="element-2665">
            {errors.general && <motion.div initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3" data-id="element-2666">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" data-id="element-2667" />
                <p className="text-sm text-red-600 font-medium" data-id="element-2668">
                  {errors.general}
                </p>
              </motion.div>}

            <AnimatePresence mode="wait" data-id="element-2669">
              {step === 1 && <motion.form key="step1" initial={{
              opacity: 0,
              x: isRTL ? -20 : 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: isRTL ? 20 : -20
            }} className="space-y-4 sm:space-y-5" onSubmit={handleInitialSubmit} data-id="element-2670">
                  <Input label={t('auth.fullName')} name="name" value={formData.name} onChange={handleInputChange} required isRTL={isRTL} data-id="element-2671" />

                  <div data-id="element-2672">
                    <Input label={t('auth.email')} type="email" name="email" value={formData.email} onChange={handleInputChange} required isRTL={isRTL} error={errors.email} data-id="element-2673" />
                    {errors.email && <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1.5" data-id="element-2674">
                        <AlertCircle className="w-4 h-4" data-id="element-2675" />
                        {errors.email}
                      </p>}
                  </div>

                  {/* Phone with Country Selector */}
                  <div data-id="element-2676">
                    <label className={`block text-sm font-medium text-gray-700 mb-1.5 ${isRTL ? 'text-right' : 'text-left'}`} data-id="element-2677">
                      {t('auth.phone')}
                    </label>
                    <div className="flex gap-2" data-id="element-2678">
                      {/* Country Selector */}
                      <div className="relative" ref={countryRef} data-id="element-2679">
                        <button type="button" onClick={() => setIsCountryOpen(!isCountryOpen)} className="flex items-center gap-1.5 px-3 py-2.5 sm:py-3 bg-gray-50 border border-gray-200 rounded-xl hover:bg-gray-100 transition-colors min-w-[100px] sm:min-w-[120px]" data-id="element-2680">
                          <span className="text-xl sm:text-2xl" data-id="element-2681">
                            {selectedCountry.flag}
                          </span>
                          <span className="text-sm text-gray-600 font-medium" data-id="element-2682">
                            {selectedCountry.dialCode}
                          </span>
                          <ChevronDown size={14} className={`text-gray-400 transition-transform ml-auto ${isCountryOpen ? 'rotate-180' : ''}`} data-id="element-2683" />
                        </button>

                        <AnimatePresence data-id="element-2684">
                          {isCountryOpen && <motion.div initial={{
                        opacity: 0,
                        y: -10
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} exit={{
                        opacity: 0,
                        y: -10
                      }} className={`absolute top-full mt-2 ${isRTL ? 'right-0' : 'left-0'} bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50 min-w-[240px] max-h-[300px] overflow-y-auto`} data-id="element-2685">
                              <div className="px-3 py-2 text-xs font-bold text-gray-500 bg-gray-50 sticky top-0 z-10" data-id="element-2686">
                                {isRTL ? 'الدول الأساسية' : 'Primary Countries'}
                              </div>
                              {countries.slice(0, 3).map(country => <button key={country.code} type="button" onClick={() => {
                          setSelectedCountry(country);
                          setIsCountryOpen(false);
                          setFormData(prev => ({
                            ...prev,
                            phone: ''
                          }));
                        }} className={`w-full flex items-center gap-3 px-4 py-3 text-sm hover:bg-gray-50 transition-colors ${selectedCountry.code === country.code ? 'bg-purple-50 text-[#4f1964]' : 'text-gray-700'}`} data-id="element-2687">
                                  <span className="text-xl" data-id="element-2688">
                                    {country.flag}
                                  </span>
                                  <span className="font-medium" data-id="element-2689">
                                    {isRTL ? country.nameAr : country.name}
                                  </span>
                                  <span className={`text-gray-400 text-xs ${isRTL ? 'mr-auto' : 'ml-auto'}`} data-id="element-2690">
                                    {country.dialCode}
                                  </span>
                                </button>)}
                              <div className="px-3 py-2 text-xs font-bold text-gray-500 bg-gray-50 border-t border-gray-100 sticky top-[32px] z-10" data-id="element-2691">
                                {isRTL ? 'دول أخرى' : 'Other Countries'}
                              </div>
                              {countries.slice(3).map(country => <button key={country.code} type="button" onClick={() => {
                          setSelectedCountry(country);
                          setIsCountryOpen(false);
                          setFormData(prev => ({
                            ...prev,
                            phone: ''
                          }));
                        }} className={`w-full flex items-center gap-3 px-4 py-3 text-sm hover:bg-gray-50 transition-colors ${selectedCountry.code === country.code ? 'bg-purple-50 text-[#4f1964]' : 'text-gray-700'}`} data-id="element-2692">
                                  <span className="text-xl" data-id="element-2693">
                                    {country.flag}
                                  </span>
                                  <span className="font-medium" data-id="element-2694">
                                    {isRTL ? country.nameAr : country.name}
                                  </span>
                                  <span className={`text-gray-400 text-xs ${isRTL ? 'mr-auto' : 'ml-auto'}`} data-id="element-2695">
                                    {country.dialCode}
                                  </span>
                                </button>)}
                            </motion.div>}
                        </AnimatePresence>
                      </div>

                      {/* Phone Input */}
                      <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} placeholder={selectedCountry.placeholder} required className={`flex-1 px-4 py-2.5 sm:py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4f1964] focus:border-transparent transition-all text-sm sm:text-base ${errors.phone ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50/50'}`} dir="ltr" data-id="element-2696" />
                    </div>
                    {errors.phone && <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1.5" data-id="element-2697">
                        <AlertCircle className="w-4 h-4" data-id="element-2698" />
                        {errors.phone}
                      </p>}
                  </div>

                  <div data-id="element-2699">
                    <div className="relative" data-id="element-2700">
                      <Input label={t('auth.password')} type={showPassword ? 'text' : 'password'} name="password" value={formData.password} onChange={handleInputChange} required isRTL={isRTL} data-id="element-2701" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className={`absolute ${isRTL ? 'left-3' : 'right-3'} top-9 text-gray-400 hover:text-gray-600 transition-colors`} data-id="element-2702">
                        {showPassword ? <EyeOff className="w-5 h-5" data-id="element-2703" /> : <Eye className="w-5 h-5" data-id="element-2704" />}
                      </button>
                    </div>

                    {/* Password Strength Indicator */}
                    {formData.password && <div className="mt-3 p-3 bg-gray-50 rounded-lg" data-id="element-2705">
                        <div className="flex items-center gap-2 mb-2" data-id="element-2706">
                          <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden" data-id="element-2707">
                            <motion.div initial={{
                        width: 0
                      }} animate={{
                        width: passwordStrength === 'strong' ? '100%' : passwordStrength === 'medium' ? '66%' : '33%'
                      }} className={`h-full ${getStrengthColor()}`} data-id="element-2708" />
                          </div>
                          <span className={`text-xs font-bold ${passwordStrength === 'strong' ? 'text-green-600' : passwordStrength === 'medium' ? 'text-yellow-600' : 'text-red-600'}`} data-id="element-2709">
                            {getStrengthText()}
                          </span>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5" data-id="element-2710">
                          {[{
                      check: formData.password.length >= 8,
                      text: t('password.minLength')
                    }, {
                      check: /[A-Z]/.test(formData.password),
                      text: t('password.uppercase')
                    }, {
                      check: /[a-z]/.test(formData.password),
                      text: t('password.lowercase')
                    }, {
                      check: /[0-9]/.test(formData.password),
                      text: t('password.number')
                    }, {
                      check: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.password),
                      text: t('password.special')
                    }].map((req, i) => <div key={i} className="flex items-center gap-1.5" data-id="element-2711">
                              {req.check ? <Check className="w-3 h-3 text-green-500" data-id="element-2712" /> : <div className="w-3 h-3 rounded-full border border-gray-300" data-id="element-2713" />}
                              <span className={`text-xs ${req.check ? 'text-green-700 font-medium' : 'text-gray-500'}`} data-id="element-2714">
                                {req.text}
                              </span>
                            </div>)}
                        </div>
                      </div>}
                  </div>

                  {/* OTP Method Selection */}
                  <div className="space-y-3 pt-2" data-id="element-2715">
                    <label className={`block text-sm font-medium text-gray-700 ${isRTL ? 'text-right' : 'text-left'}`} data-id="element-2716">
                      {t('auth.verificationMethod')}
                    </label>
                    <div className="grid grid-cols-2 gap-3 sm:gap-4" data-id="element-2717">
                      <button type="button" onClick={() => setOtpMethod('whatsapp')} className={`relative flex flex-col items-center justify-center gap-2 px-4 py-3 sm:py-4 rounded-xl border-2 transition-all ${otpMethod === 'whatsapp' ? 'border-[#4f1964] bg-purple-50 text-[#4f1964] shadow-sm' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-2718">
                        {otpMethod === 'whatsapp' && <div className="absolute top-2 right-2 w-2 h-2 bg-[#4f1964] rounded-full" data-id="element-2719" />}
                        <MessageSquare className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-2720" />
                        <span className="font-bold text-xs sm:text-sm" data-id="element-2721">
                          {t('auth.whatsapp')}
                        </span>
                      </button>
                      <button type="button" onClick={() => setOtpMethod('email')} className={`relative flex flex-col items-center justify-center gap-2 px-4 py-3 sm:py-4 rounded-xl border-2 transition-all ${otpMethod === 'email' ? 'border-[#4f1964] bg-purple-50 text-[#4f1964] shadow-sm' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-2722">
                        {otpMethod === 'email' && <div className="absolute top-2 right-2 w-2 h-2 bg-[#4f1964] rounded-full" data-id="element-2723" />}
                        <Mail className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-2724" />
                        <span className="font-bold text-xs sm:text-sm" data-id="element-2725">
                          {t('auth.emailMethod')}
                        </span>
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 text-center bg-gray-50 py-2 rounded-lg" data-id="element-2726">
                      {otpMethod === 'whatsapp' ? t('auth.otpViaWhatsapp') : t('auth.otpViaEmail')}
                    </p>
                  </div>

                  <div className="pt-2" data-id="element-2727">
                    <Button type="submit" fullWidth isLoading={isLoading} size="lg" disabled={!validatePassword(formData.password, isRTL).isValid} className="bg-[#4f1964] hover:bg-[#3d1350] text-white shadow-lg shadow-purple-200 py-3 sm:py-4 text-base" data-id="element-2728">
                      {t('common.continue')}
                      {isRTL ? <ArrowLeft className="mr-2 w-5 h-5" data-id="element-2729" /> : <ArrowRight className="ml-2 w-5 h-5" data-id="element-2730" />}
                    </Button>
                  </div>
                </motion.form>}

              {step === 2 && <motion.div key="step2" initial={{
              opacity: 0,
              x: isRTL ? -20 : 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: isRTL ? 20 : -20
            }} data-id="element-2731">
                  <div className="mb-6 text-center" data-id="element-2732">
                    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${otpMethod === 'whatsapp' ? 'bg-green-50 border border-green-200' : 'bg-blue-50 border border-blue-200'}`} data-id="element-2733">
                      {otpMethod === 'whatsapp' ? <MessageSquare className="w-4 h-4 text-green-600" data-id="element-2734" /> : <Mail className="w-4 h-4 text-blue-600" data-id="element-2735" />}
                      <span className={`text-sm font-bold ${otpMethod === 'whatsapp' ? 'text-green-700' : 'text-blue-700'}`} data-id="element-2736">
                        {otpMethod === 'whatsapp' ? t('auth.sentViaWhatsapp') : t('auth.sentViaEmail')}
                      </span>
                    </div>
                  </div>
                  <OTPVerification phone={getFullPhoneNumber()} email={formData.email} method={otpMethod} onComplete={handleOTPComplete} onResend={handleResendOTP} onSwitchMethod={handleSwitchMethod} data-id="element-2737" />
                </motion.div>}

              {step === 3 && <motion.div key="step3" initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} className="text-center py-12" data-id="element-2738">
                  <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner" data-id="element-2739">
                    <CheckCircle className="w-12 h-12 text-green-600" data-id="element-2740" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-2741">
                    {t('auth.registerSuccess')}
                  </h3>
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-full mb-6" data-id="element-2742">
                    <span className="text-green-700 font-bold text-sm" data-id="element-2743">
                      {t('auth.freeTrialActivated')}
                    </span>
                  </div>
                  <p className="text-gray-500 animate-pulse" data-id="element-2744">
                    {t('auth.redirecting')}
                  </p>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* RIGHT SIDE - DECORATIVE (Hidden on mobile) */}
      <div className="hidden lg:flex flex-1 relative bg-white overflow-hidden items-center justify-center p-12 border-l border-gray-100" data-id="element-2745">
        {/* Content Container */}
        <div className="relative z-10 max-w-md w-full" data-id="element-2746">
          {/* Trust Card */}
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2,
          duration: 0.8
        }} className="bg-white border-2 border-gray-200 rounded-3xl p-8 shadow-sm" data-id="element-2747">
            <div className="flex items-center gap-4 mb-6" data-id="element-2748">
              <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center" data-id="element-2749">
                <img src="https://cdn.magicpatterns.com/uploads/faWT17kE43WdqrZMBmZAS3/Operines-Icon-color1_(2).png" alt="Icon" className="w-8 h-8 object-contain" data-id="element-2750" />
              </div>
              <div data-id="element-2751">
                <h3 className="font-bold text-lg text-gray-900" data-id="element-2752">
                  {isRTL ? 'محبوب من المستخدمين' : 'Loved by users everywhere'}
                </h3>
                <div className="flex gap-1 text-yellow-400 mt-1" data-id="element-2753">
                  {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-4 h-4 fill-current" data-id="element-2754" />)}
                </div>
              </div>
            </div>

            <div className="space-y-3" data-id="element-2755">
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl" data-id="element-2756">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-600" data-id="element-2757">
                  <Users className="w-5 h-5" data-id="element-2758" />
                </div>
                <div data-id="element-2759">
                  <div className="text-2xl font-bold text-gray-900" data-id="element-2760">+500</div>
                  <div className="text-sm text-gray-500" data-id="element-2761">
                    {isRTL ? 'عميل نشط' : 'Active Clients'}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl" data-id="element-2762">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600" data-id="element-2763">
                  <TrendingUp className="w-5 h-5" data-id="element-2764" />
                </div>
                <div data-id="element-2765">
                  <div className="text-2xl font-bold text-gray-900" data-id="element-2766">99.9%</div>
                  <div className="text-sm text-gray-500" data-id="element-2767">
                    {isRTL ? 'وقت التشغيل' : 'Uptime'}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl" data-id="element-2768">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-[#4f1964]" data-id="element-2769">
                  <MessageSquare className="w-5 h-5" data-id="element-2770" />
                </div>
                <div data-id="element-2771">
                  <div className="text-2xl font-bold text-gray-900" data-id="element-2772">+10K</div>
                  <div className="text-sm text-gray-500" data-id="element-2773">
                    {isRTL ? 'رسالة يومياً' : 'Messages Daily'}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-5 border-t border-gray-100 text-center" data-id="element-2774">
              <p className="text-sm text-gray-500 italic" data-id="element-2775">
                "
                {isRTL ? 'أفضل منصة لأتمتة واتساب في الشرق الأوسط' : 'The best WhatsApp automation platform in the Middle East'}
                "
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>;
}